import { FileInput, useUppy } from "@uppy/react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useRef, useState } from "react";
import { ProjectFile, ProjectFileType } from "generated/graphql";
import { CircularProgress } from "@mui/material";
import { useCreateProjectFileFolder } from "hooks/projectFiles/useCreateProjectFileFolder";
import useGenerateUploadProjectFileUrl from "hooks/projectFiles/useGenerateUploadProjectFileUrl";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import useAddProjectFile from "hooks/projectFiles/useAddProjectFile";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import useDeleteProjectFile from "hooks/projectFiles/useDeleteProjectFile";
import { ReactNode } from "react";
import Modal from "modules/Modal/Modal";
import MDTypography from "components/MDTypography";

const AddFileOrFolder = ({
  fileView,
  loading,
  lastSelectedItem,
  fileById,
  folderView,
  onFolderCreated,
  showAddFolder = true,
  showUpload = true,
  showDeleteFolder = false,
  onFolderDeleted,
  trailingActions,
}: {
  fileView?: boolean;
  loading?: boolean;
  lastSelectedItem: string;
  fileById: ProjectFile;
  folderView?: boolean;
  onFolderCreated?: (folderId: string) => void;
  showAddFolder?: boolean;
  showUpload?: boolean;
  showDeleteFolder?: boolean;
  onFolderDeleted?: () => void;
  trailingActions?: ReactNode;
}) => {
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const fileByIdRef = useRef<null | ProjectFile>();
  const [addFolderInput, setAddFolderInput] = useState("");

  fileByIdRef.current = fileById;

  const [
    generateUploadProjectFileUrl,
    { getData, isSuccess: generateUrlSuccess },
  ] = useGenerateUploadProjectFileUrl();
  const [addProjectFile, { loading: loadingAddProjectFile }] = useAddProjectFile();
  const [createProjectFileFolder] = useCreateProjectFileFolder();
  const [deleteProjectFile] = useDeleteProjectFile();

  const pathRef = useRef("");
  const resetAddFolderInput = () => {
    setShowAddFolderModal(false);
    setAddFolderInput("");
  };
  const handleAddFolderInput = () => {
    createProjectFileFolder({
      variables: {
        name: addFolderInput,
        ...(lastSelectedItem && {
          parentFolderId:
            fileById?.type === ProjectFileType.FOLDER ? fileById.id : fileById?.parentFolderId,
        }),
      },
    }).then((result) => {
      const createdFolder = getCreatedFolder(result);
      if (createdFolder?.id) {
        onFolderCreated?.(createdFolder.id);
        resetAddFolderInput();
      }
    });
  };

  const getCreatedFolder = (result: any) => {
    return result?.data?.createProjectFileFolder ?? null;
  };

  const uppy = useUppy(() => {
    const _uppy = new Uppy({
      id: `organizationFile`,
      autoProceed: true,
      allowMultipleUploads: false,
      allowMultipleUploadBatches: false,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ["image/*", ".jpg", ".jpeg", ".png", ".pdf", ".heic", ".mp4", ".mov"],
      },
    }).use(AwsS3, {
      async getUploadParameters(file) {
        const result = await generateUploadProjectFileUrl({
          variables: {
            contentType: file.type,
            filename: file.name,
          },
        });
        const success = generateUrlSuccess(result.data);
        if (success) {
          const data = getData(result);

          pathRef.current = data.path;
          return {
            method: data.method as "PUT",
            url: data.url,
            fields: {},
            headers: {
              "Content-Type": data.contentType,
              ...(data.contentDisposition && { "Content-Disposition": data.contentDisposition }),
            },
          };
        } else {
          throw new Error("Could not upload file");
        }
      },
    });

    _uppy.on("upload-success", (file, response) => {
      if (response.status === 200) {
        addProjectFile({
          variables: {
            name: file.name,
            path: pathRef.current,
            parentFolderId:
              fileByIdRef.current?.type === ProjectFileType.FOLDER
                ? fileByIdRef.current.id
                : fileByIdRef.current?.parentFolderId,
          },
        });

        _uppy.removeFile(file.id);
      }
    });

    _uppy.on("restriction-failed", (file, error) => {
      if (error) {
        //
      }
    });

    _uppy.on("upload-error", (file) => {
      if (file) {
        // setErrorMessage("An error has ocurred while uploading this file");
        _uppy.removeFile(file.id);
      }
      // setBtnColor("info");
    });

    return _uppy;
  });

  const shouldDisableAddFolderButton = addFolderInput.trim().length < 1;
  const canDeleteFolder =
    showDeleteFolder && fileById?.id && fileById?.type === ProjectFileType.FOLDER;

  const handleDeleteFolder = async () => {
    if (!canDeleteFolder) {
      return;
    }

    await deleteProjectFile({
      variables: {
        id: fileById.id,
      },
    });
    setDeleteModalOpen(false);
    onFolderDeleted?.();
  };

  return (
    <>
      {loadingAddProjectFile && (
        <MDBox textAlign="center">
          <CircularProgress />
        </MDBox>
      )}
      <MDBox
        p={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        flexWrap="nowrap"
      >
        <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="nowrap">
          {showAddFolder && (
            <MDButton
              variant="contained"
              type="button"
              color="blue"
              size="small"
              disabled={loading}
              onClick={() => setShowAddFolderModal(true)}
              sx={{ whiteSpace: "nowrap", px: 1.5, py: 0.75, minHeight: "32px" }}
            >
              Add folder
            </MDButton>
          )}
          {showDeleteFolder && (
            <MDButton
              variant="contained"
              type="button"
              color="error"
              size="small"
              disabled={!canDeleteFolder}
              onClick={() => setDeleteModalOpen(true)}
              sx={{ whiteSpace: "nowrap", px: 1.5, py: 0.75, minHeight: "32px" }}
            >
              Delete folder
            </MDButton>
          )}
        </MDBox>
        {showUpload && !folderView && (
          <MDBox
            sx={{
              ".uppy-FileInput-container": {
                marginBottom: 0,
              },
              ".uppy-FileInput-btn": {
                fontWeight: "bold",
                fontSize: "12px",
                padding: "8px 14px",
                minHeight: "32px",
                borderRadius: "0.5rem",
                background: (theme) => theme.palette.blue.main,
                color: "#fff",
                border: "none",
              },
              ".uppy-FileInput-btn:hover": {
                filter: "brightness(0.96)",
              },
            }}
          >
            <FileInput
              uppy={uppy}
              pretty
              locale={{
                strings: { chooseFiles: "Add files" },
              }}
            />
          </MDBox>
        )}
        {trailingActions && (
          <MDBox display="flex" alignItems="center" gap={0.75} flexWrap="nowrap">
            {trailingActions}
          </MDBox>
        )}
      </MDBox>
      <ActionConfirmDialog
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        handleAccept={handleDeleteFolder}
        handleCancel={() => setDeleteModalOpen(false)}
        title="Confirmation"
        content={
          canDeleteFolder
            ? `Delete folder ${fileById.name}? ${
                fileById.children?.length ? "All subfiles and subfolders will also be deleted" : ""
              }`
            : ""
        }
      />
      <Modal
        open={showAddFolderModal}
        onClose={resetAddFolderInput}
        styles={{ width: "420px", height: "auto", maxHeight: "unset", overflow: "hidden" }}
      >
        <MDBox p={3}>
          <MDTypography variant="h6">Add folder</MDTypography>
          <MDBox mt={2}>
            <MDInput
              fullWidth
              placeholder="Folder name"
              value={addFolderInput}
              onChange={(e) => setAddFolderInput(e.target.value)}
            />
          </MDBox>
          <MDBox mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton variant="text" color="secondary" onClick={resetAddFolderInput}>
              Cancel
            </MDButton>
            <MDButton
              variant="contained"
              color="blue"
              disabled={shouldDisableAddFolderButton}
              onClick={handleAddFolderInput}
            >
              Save folder
            </MDButton>
          </MDBox>
        </MDBox>
      </Modal>
    </>
  );
};

export default AddFileOrFolder;
